// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';

import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props ={|
  fieldName: string,
|};

export function FieldAddress({ fieldName }: Props): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsTextField(fieldName);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError(fieldName);
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const  placeholder = getPlaceholder(country);

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('address');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="address-street-name">
      <label htmlFor={inputId}>
        {getFieldLabelByCountry(country)}
      </label>

      <TextField
        id={inputId}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-street-name"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessageByCountry validationError={validationError} country={country} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
  country: string,
|};

function ValidationMessageByCountry({ validationError, country }: ValidationMessageProps): Node {
  switch (country) {
    case 'co':
    case 'pe':
    case 'cl':
    case 'uy':
      return <ValidationMessagesSpanish validationError={validationError} />;

    default:
      throw new Error(`Field Address: error messages not mapped for country: ${country}`);
  }
}

type ValidationMessageForLanguageProps = {|
  validationError: UserInputValidationError,
|};

function getPlaceholder(country: string): ?string {
  switch (country) {
    case 'co':
    case 'uy':
    case 'cl':
    case 'pe':
      return 'Calle, Avenida o Otro';
    default:
      null;
  }
}

function ValidationMessagesSpanish({ validationError }: ValidationMessageForLanguageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Calle es requerido
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El calle es inválido
        </ErrorLabel>
      );
  }
}

function getFieldLabelByCountry(country: string): string {
  switch (country) {
    case 'cr':
    case 'py':
    case 'pa':
    case 'do':
    case 'sv':
    case 'gt':
      return 'Calle';

    case 'pe':
    case 'cl':
    case 'co':
    case 'uy':
      return 'Dirección';

    case 'za':
      return 'Address';

    default:
      throw new Error(`Field Address: Label not mapped for country ${country}`);
  }
}
